/**
 * OCT20 WS-ROLL (FD-11) — "Rollover Decision Required" rows for TODAY's Dayboard. Pure; no I/O.
 *
 * The source of truth is the server: an open (state 'proposed') rollover decision whose day has already ended —
 * raised for an unfinished last rollover day by raise_unfinished_rollover_tail_decisions (pg_cron, no browser), or
 * any other end-of-day question nobody has answered for a past day. The rows are a LIST beside the lanes, never a
 * card inside a lane: the work keeps its own (past) date and holds no time on today's schedule until a person
 * decides — so it cannot consume future capacity just by being shown (FD-11).
 */

export interface DecisionConversionRow {
  id: string;
  origin_ticket_id: string;
  origin_date: string;
  kind: string;
  detection: Record<string, unknown> | null;
  acknowledged_at: string | null;
}

export interface DecisionTicketRow {
  id: string;
  simple_ticket_number: number | null;
  customer_name: string | null;
  status: string;
  scheduled_date: string | null;
  deleted_at?: string | null;
}

export interface DecisionRequiredRow {
  conversionId: string;
  ticketId: string;
  ticketLabel: string;
  customerName: string | null;
  lastApprovedDate: string;
  daysOverdue: number;
  started: boolean;
  acknowledged: boolean;
  source: 'stale_chain_tail' | 'stale_single_day' | 'other';
}

// OCT20A WS-ROLL2 (R3-B): the founder's wording for unresolved work from an ended day, single-day or multiday.
export const DECISION_REQUIRED_LABEL = 'Scheduling Decision Required';

function daysBetween(fromIso: string, toIso: string): number {
  const a = Date.parse(`${fromIso}T00:00:00Z`);
  const b = Date.parse(`${toIso}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

export function ticketLabel(t: Pick<DecisionTicketRow, 'id' | 'simple_ticket_number'>): string {
  return t.simple_ticket_number != null ? `#TKT-${String(t.simple_ticket_number).padStart(4, '0')}` : `#${t.id.slice(0, 8)}`;
}

/**
 * Rows for `today` (the shop's local date, YYYY-MM-DD): open decisions for a day BEFORE today whose ticket is still
 * live. Oldest day first. A decision for today or later is the ordinary end-of-day panel's business, not this list.
 */
export function buildDecisionRequiredRows(
  conversions: DecisionConversionRow[],
  tickets: DecisionTicketRow[],
  today: string,
): DecisionRequiredRow[] {
  const byId = new Map(tickets.map((t) => [t.id, t]));
  const seen = new Set<string>();
  const rows: DecisionRequiredRow[] = [];
  for (const c of conversions) {
    if (!c.origin_date || c.origin_date >= today) continue;
    const t = byId.get(c.origin_ticket_id);
    if (!t || t.deleted_at || !['Active', 'Paused'].includes(t.status)) continue;
    // The ticket must still sit on the day the question is about (moved work is no longer stranded).
    if (t.scheduled_date && t.scheduled_date !== c.origin_date) continue;
    const key = `${c.origin_ticket_id}:${c.origin_date}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({
      conversionId: c.id,
      ticketId: t.id,
      ticketLabel: ticketLabel(t),
      customerName: t.customer_name ?? null,
      lastApprovedDate: c.origin_date,
      daysOverdue: daysBetween(c.origin_date, today),
      started: c.kind === 'started_eod',
      acknowledged: !!c.acknowledged_at,
      source: c.detection?.source === 'stale_chain_tail' || c.detection?.source === 'stale_single_day'
        ? (c.detection.source as 'stale_chain_tail' | 'stale_single_day') : 'other',
    });
  }
  return rows.sort((a, b) => a.lastApprovedDate.localeCompare(b.lastApprovedDate) || a.ticketId.localeCompare(b.ticketId));
}

export function describeDecisionRequiredRow(r: DecisionRequiredRow): string {
  const day = r.daysOverdue === 1 ? 'yesterday' : `${r.daysOverdue} days ago`;
  if (r.source === 'stale_single_day') {
    return `Scheduled day ${r.lastApprovedDate} (${day}) ended with the work ${r.started ? 'started and ' : ''}unfinished`;
  }
  return r.source === 'stale_chain_tail'
    ? `Last approved day ${r.lastApprovedDate} (${day}) ended with the work ${r.started ? 'started and ' : ''}unfinished`
    : `End-of-day decision for ${r.lastApprovedDate} (${day}) was never made`;
}
